/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, cloneDeep } from 'lodash';
import PropTypes from 'prop-types';

import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { ctrlDefaults } from '../../../../shared/form-lib';
import { selectCtrlIdLabelMap } from '../../selectors';
import { controlValidate } from '../../../form/utils';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  TextAreaWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Field,
} from '../../common-field-styles';

const getWordCount = (ctrl) => {
  if (ctrl.value && (ctrl.props.min_word_length || ctrl.props.max_word_length)) {
    return ctrl.value.trim().split(/\s+/).length;
  }
};

const LongText = ({ ctrl, formDesign }) => {
  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError ?? '');
  const { inBuilder, inCustomise, inForm, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const [inputValue, setInputValue] = useState(ctrl.value ?? '');
  const responseStore = form?.response_store ?? 'formplus';
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];
  const wordCount = inForm ? getWordCount(ctrl) : 0;
  const [newCtrl, setNewCtrl] = useState(ctrl);
  const dispatch = useDispatch();

  useEffect(() => {
    setInputValue(ctrl.value ?? '');
  }, [ctrl.value]);

  useEffect(() => {
    setCtrlValidationError(ctrl._validationError ?? '');
  }, [ctrl._validationError]);

  const saveUpdatedCtrl = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          newValue,
          property: 'value',
          page: currentPageNo - 1,
          ctrlId: ctrl.internal_id,
        })
      );
    }, 200)
  );

  const handleInputValue = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setNewCtrl((prevCtrl) => ({ ...cloneDeep(prevCtrl), value: newValue }));
  };

  const handleBlur = () => {
    controlValidate({
      setCtrlValidationError,
      userCustomTexts,
      ctrl: newCtrl,
      responseStore
    });
  };

  const handleChange = (e) => {
    handleInputValue(e);
    const newValue = e.target.value;
    if (ctrl.value !== newValue) {
      saveUpdatedCtrl.current(newValue);
    }
  };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
          <LabelWrapper
            notInBuilder={!inBuilder}
            required={ctrl.props.required}
            className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <label
              htmlFor={`fpl-${ctrl.internal_id}`}
              dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
            />
            <SpecialPropsIndicators ctrl={ctrl} />
          </LabelWrapper>

          <InputWrapper className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <TextAreaWrapper
              value={inputValue}
              onBlur={handleBlur}
              onChange={handleChange}
              notInBuilder={!inBuilder}
              id={`fpl-${ctrl.internal_id}`}
              aria-describedby="descriptive text"
              disabled={ctrl.props.readonly || inCustomise || inBuilder}
              placeholder={ctrl.props.placeholder || 'Type your answer here'}
            />

            <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
              {ctrl.props.subtext}
              {wordCount > 0 && <span id="words">{`${wordCount} word${wordCount > 1 ? 's' : ''}`}</span>}
            </SubtextWrapper>
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

LongText.defaultProps = {
  formDesign: null,
};

LongText.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default LongText;
